import { useState, useEffect, useCallback } from 'react';
import { userService } from '@/services/amai';
import { UserPackageResponse } from '@/services/types';
import { useMessage } from '@/hooks/useMessage';
import { eventBus } from '@/utils/eventBus';

export interface MembershipPackageInfo {
  planName: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  daysRemaining: number;
}

export interface CurrentMembershipInfo {
  packages: MembershipPackageInfo[];
  freePackageRights: string[];
}

/**
 * 格式化日期显示
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * 计算剩余天数
 */
const calculateDaysRemaining = (endTime: string): number => {
  try {
    const endDate = new Date(endTime);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  } catch (error) {
    return 0;
  }
};

/**
 * 检查套餐是否有效
 */
const isPackageActive = (endTime: string): boolean => {
  try {
    const endDate = new Date(endTime);
    const now = new Date();
    return endDate.getTime() > now.getTime();
  } catch (error) {
    return false;
  }
};

/**
 * 转换用户套餐数据
 */
const transformUserPackageData = (data: UserPackageResponse): CurrentMembershipInfo | null => {
  const packages: MembershipPackageInfo[] = [];

  // 如果有已购买的套餐，转换所有套餐
  if (data.list && data.list.length > 0) {
    // 按结束时间排序，有效套餐在前
    const sortedPackages = [...data.list].sort((a, b) => {
      const aActive = isPackageActive(a.end_time);
      const bActive = isPackageActive(b.end_time);

      // 有效套餐优先
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      // 同样状态的按结束时间排序
      return new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
    });

    // 转换所有套餐
    sortedPackages.forEach(pkg => {
      packages.push({
        planName: pkg.package_name,
        startTime: formatDate(pkg.start_time),
        endTime: formatDate(pkg.end_time),
        isActive: isPackageActive(pkg.end_time),
        daysRemaining: calculateDaysRemaining(pkg.end_time),
      });
    });
  }

  // 当list为null或空时，如果有免费套餐权益，添加免费用户信息
  if (
    (!data.list || data.list.length === 0) &&
    data.free_package?.rights &&
    data.free_package.rights.length > 0
  ) {
    packages.push({
      planName: '免费用户',
      startTime: '永久有效',
      endTime: '永久有效',
      isActive: true,
      daysRemaining: -1, // -1 表示永久有效
    });
  }

  // 如果有任何套餐或免费权益，返回数据
  if (packages.length > 0 || (data.free_package?.rights && data.free_package.rights.length > 0)) {
    return {
      packages,
      freePackageRights: data.free_package?.rights || [],
    };
  }

  return null;
};

/**
 * 用户套餐管理Hook
 */
export const useUserPackage = () => {
  const [membershipInfo, setMembershipInfo] = useState<CurrentMembershipInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { error: showError } = useMessage();

  /**
   * 获取用户套餐信息
   */
  const fetchUserPackage = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.getUserPackage();

      if (response.code === 0 && response.data) {
        const transformedData = transformUserPackageData(response.data);
        setMembershipInfo(transformedData);
      } else {
        const errorMsg = response.message || '获取用户套餐信息失败';
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = '网络错误，请稍后重试';
      setError(errorMsg);
      showError(errorMsg);
      console.error('Failed to fetch user package:', err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * 刷新用户套餐信息
   */
  const refreshUserPackage = useCallback(() => {
    fetchUserPackage();
  }, [fetchUserPackage]);

  /**
   * 清除错误状态
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 组件挂载时获取数据，并监听刷新事件
  useEffect(() => {
    fetchUserPackage();

    const unsubscribe = eventBus.on('refreshUserPackage', fetchUserPackage);

    return () => {
      unsubscribe();
    };
  }, [fetchUserPackage]);

  return {
    membershipInfo,
    loading,
    error,
    refreshUserPackage,
    clearError,
  };
};

/**
 * 获取套餐状态的显示文本
 */
export const getMembershipStatusText = (info: MembershipPackageInfo): string => {
  if (!info.isActive) {
    return '已过期';
  }

  if (info.daysRemaining === -1) {
    return '永久有效';
  }

  if (info.daysRemaining === 0) {
    return '今日到期';
  }

  return `剩余${info.daysRemaining}天`;
};

/**
 * 获取套餐状态的颜色
 */
export const getMembershipStatusColor = (info: MembershipPackageInfo): string => {
  if (!info.isActive) {
    return '#ff6b6b'; // 红色 - 已过期
  }

  if (info.daysRemaining === -1) {
    return '#28a745'; // 绿色 - 永久有效
  }

  if (info.daysRemaining <= 7) {
    return '#ffc107'; // 黄色 - 即将到期
  }

  return '#28a745'; // 绿色 - 正常
};
